import { useState, useEffect, type FormEvent } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { AuthLayout, Input, Button, HiyaLogo, consumeSessionExpiredReason } from "@hiya/ui";
import { signIn, getCurrentUser } from "../../auth/auth-service";

export function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const sessionExpiredFromState =
    (location.state as { sessionExpired?: boolean })?.sessionExpired ?? false;
  // sessionStorage flag set by handleUnauthorized survives the hard reload
  // that drops React Router state. HH-200 Unit 28: idle-timeout 401.
  const [sessionExpiredFromStorage] = useState(() => consumeSessionExpiredReason() !== null);
  const sessionExpired = sessionExpiredFromState || sessionExpiredFromStorage;
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!sessionExpired) {
      getCurrentUser().then((u) => {
        if (u) navigate("/dashboard", { replace: true });
      });
    }
  }, [navigate, sessionExpired]);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError("");

    if (!email || !password) {
      setError("Please fill in all fields.");
      return;
    }

    setLoading(true);
    try {
      const result = await signIn(email, password);
      switch (result.nextStep) {
        case "CONFIRM_SIGN_IN_WITH_EMAIL_CODE":
          navigate("/login/otp", { state: { email } });
          break;
        case "CONFIRM_SIGN_IN_WITH_NEW_PASSWORD":
          navigate("/login/new-password", {
            state: { email, missingAttributes: result.missingAttributes },
          });
          break;
        case "DONE":
          navigate("/dashboard");
          break;
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : "";
      if (message.includes("disabled") || message.includes("locked") || message.includes("Password attempts exceeded")) {
        navigate("/login/locked");
        return;
      }
      const errMsg = err instanceof Error ? err.message : "";
      if (errMsg.includes("Unsupported authentication step")) {
        setError("Something went wrong. Please try again or contact support.");
      } else {
        setError(
          "That email or password doesn't look right. Please check and try again.",
        );
      }
    } finally {
      setLoading(false);
    }
  }

  return (
    <AuthLayout
      title="Welcome to HIYA Admin"
      subtitle="Please enter your credentials to log in"
      icon={<HiyaLogo />}
    >
      <form onSubmit={handleSubmit} className="flex flex-col gap-8 w-full">
        {sessionExpired && (
          <div className="rounded-2xl bg-bg-secondary px-4 py-3 text-sm text-input-hint-error text-center">
            Your session has expired. Please log in again.
          </div>
        )}
        <div className="flex flex-col gap-2 w-full">
          <Input
            type="email"
            placeholder="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            error={error || undefined}
          />
          <Input
            type="password"
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </div>
        <Button type="submit" disabled={loading}>
          {loading ? "Logging in..." : "Log in"}
        </Button>
      </form>
    </AuthLayout>
  );
}
